import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateProjectInput, CreateProjectInputSchema, CURRENCY } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { useSocietiesLookupQuery } from '@/features/societies/hooks/use-societies-query'
import { useUsersLookupAllQuery } from '@/features/users/hooks/use-users-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { ImageDropzone } from '@/shared/form/image-dropzone'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { MultiSelect } from '@/shared/ui/multi-select'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

const CURRENCY_LABELS: Record<string, string> = {
  USD: 'USD — Dólares',
  GTQ: 'GTQ — Quetzales'
}

type ProjectFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateProjectInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreateProjectInput) => void
  onCancel: () => void
}

/**
 * Formulario de proyecto (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend, incluido el
 * refine que exige ≥1 autorizador cuando requiresAuthorization está activo.
 */
export function ProjectForm({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: ProjectFormProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<CreateProjectInput>({
    resolver: zodResolver(CreateProjectInputSchema),
    defaultValues: {
      currency: CURRENCY.GTQ,
      requiresAuthorization: false,
      authorizerIds: [],
      active: true,
      ...defaultValues
    }
  })

  const { data: societies = [] } = useSocietiesLookupQuery()
  const { data: users = [] } = useUsersLookupAllQuery()
  const authorizerOptions = users.map((user) => ({ value: user._id, label: user.fullname }))

  const requiresAuthorization = watch('requiresAuthorization')

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="project-name">Nombre</FieldLabel>
                <Input
                  id="project-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.code)}>
                <FieldLabel htmlFor="project-code">Código</FieldLabel>
                <Input
                  id="project-code"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>
                  Único entre proyectos. Es el prefijo de los códigos de subtipos e inmuebles.
                </FieldDescription>
                <FieldError errors={[errors.code]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="society"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.society)}>
                <FieldLabel htmlFor="project-society">Sociedad</FieldLabel>
                <NativeSelect
                  id="project-society"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una sociedad
                  </NativeSelectOption>
                  {societies.map((society) => (
                    <NativeSelectOption key={society._id} value={society._id}>
                      {society.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.society]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.currency)}>
                <FieldLabel htmlFor="project-currency">Moneda</FieldLabel>
                <NativeSelect
                  id="project-currency"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  {Object.values(CURRENCY).map((value) => (
                    <NativeSelectOption key={value} value={value}>
                      {CURRENCY_LABELS[value]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.currency]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="image"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.image)}>
                <FieldLabel htmlFor="project-image">Imagen</FieldLabel>
                <ImageDropzone
                  value={field.value ?? undefined}
                  onChange={(url) => {
                    field.onChange(url ?? undefined)
                  }}
                  placeholder="Arrastra una imagen del proyecto aquí o haz clic para seleccionar"
                  acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                />
                <FieldDescription>
                  Se usa como logo en los documentos PDF generados.
                </FieldDescription>
                <FieldError errors={[errors.image]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Reservas</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="requiresAuthorization"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="project-requires-authorization">
                  Requiere autorización
                </FieldLabel>
                <Switch
                  id="project-requires-authorization"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />

          {requiresAuthorization && (
            <Controller
              control={control}
              name="authorizerIds"
              render={({ field }) => (
                <Field data-invalid={Boolean(errors.authorizerIds)}>
                  <FieldLabel htmlFor="project-authorizers">Autorizadores</FieldLabel>
                  <MultiSelect
                    id="project-authorizers"
                    options={authorizerOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder="Selecciona autorizadores"
                    searchPlaceholder="Buscar usuario..."
                    emptyText="Sin usuarios"
                  />
                  <FieldDescription>
                    Las reservas del proyecto esperarán la aprobación de estas personas.
                  </FieldDescription>
                  <FieldError errors={[errors.authorizerIds]} />
                </Field>
              )}
            />
          )}

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="project-active">Activo</FieldLabel>
                <Switch
                  id="project-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
